import React, { PureComponent } from 'react';
import isEqual from 'lodash.isequal';
import PT from 'prop-types';
import { connect } from 'react-redux';
import FiltersHeader from '../../components/FiltersHeader';
import ButtonMenu from '../../components/FilterComponents/ButtonMenu';
import FilterRangeSlider from '../../components/FilterComponents/FilterRangeSlider';
import PlainTable from '../../components/DataTable/PlainTable';
import FilterToggle from '../../components/FilterComponents/FilterToggle';
import TableControls from '../../components/TableControls';
import { positionToTitle } from '../../../lib/lookups';
import { selectWeeks } from '../../../store/reducers/season';
import { goToView } from '../../routes.global';
import {
  GAME_STATS_MATCHUPS_VIEW,
  selectGameStatsMatchupsState,
  setGameStatsMatchupsPosition,
} from './module/gameStatsMatchups';
import classes from './GameStatsMatchups.css';
import {
  selectToolDescriptionsState,
} from '../../../store/reducers/toolDescriptions';
import { ToolDescription } from '../../components/ToolDescriptions/ToolDescriptions';
import { gciPageView } from '../../../lib/analytics';

/* eslint camelcase: 0 */
/* eslint no-return-assign: 0 */

const pageTitle = 'Game Stat Matchups';

const TeamMatchupCell = ({ logo, team, text }) => (
  <span className={classes.teamMatchupCell}>
    {logo && <img src={logo} className={classes.teamlogo} alt={team} />}
    <b>{`${team} `}</b>
    {text}
  </span>
);

TeamMatchupCell.propTypes = {
  logo: PT.string,
  team: PT.string.isRequired,
  text: PT.string.isRequired,
};

TeamMatchupCell.defaultProps = {
  logo: '',
};

const uniq = a => [...new Set(a)];

const processHeaders = (matchups, positionList) => {
  const colGroups = [
    {
      name: 'TEAM',
      columns: ['matchups', 'games'],
      colLabels: ['', 'GMS'],
    },
  ];
  let allStatKeys = [];
  // extract colgroups/colLabels & assume that offensive_team,
  // defensive_team, and matchup all have the same column info
  if (
    !matchups.length ||
    !matchups[0].defensive_team ||
    !matchups[0].defensive_team.stats
  ) { return []; }
  positionList.forEach(position => {
    if ( !matchups[0].defensive_team.stats[position] ) {
      return [];
    }
    const columns = matchups[0].defensive_team.stats[position].map(
      stat => `${position}_${stat.stat_abbr}`,
    );
    const colLabels = matchups[0].defensive_team.stats[position].map(
      stat => `${stat.stat_abbr}_short`,
    );
    // Team colLabels addition into the Stats Key modal
    const teamLabels = colGroups[0].colLabels.slice(1).map(label =>
      `${label}_short`,
    );
    allStatKeys = allStatKeys.concat(colLabels, teamLabels);
    colGroups.push({
      name: positionToTitle(position, { uppercase: true }),
      columns: uniq(columns),
      colLabels: uniq(colLabels),
    });
  });

  return { colGroups, allStatKeys: [{ columns: uniq(allStatKeys) }] };
};

const processRows = matchups => {
  const allStatRows = [];
  let stripeGroup = false;
  if (
    !matchups.length ||
    !matchups[0].offensive_team ||
    !matchups[0].offensive_team.stats
  ) { return []; }
  matchups.forEach(matchup => {
    // Gains Row
    const defensiveMatchupRow = {};
    let { team_logo, team_abbr, games, stats } = matchup.defensive_team;
    defensiveMatchupRow.matchups = (
      <TeamMatchupCell logo={team_logo} team={team_abbr} text={'gains'} />
    );
    defensiveMatchupRow.games = games;
    defensiveMatchupRow.isStatRow = true;
    defensiveMatchupRow.stripeGroup = stripeGroup;
    Object.keys(stats).forEach(position => {
      stats[position].forEach(
        stat =>
          (defensiveMatchupRow[`${position}_${stat.stat_abbr}`] =
            stat.stat_value_total),
      );
    });
    // Allows Row
    const offensiveMatchupRow = {};
    ({ team_logo, team_abbr, stats, games } = matchup.offensive_team);
    offensiveMatchupRow.matchups = (
      <TeamMatchupCell logo={team_logo} team={team_abbr} text={'allows'} />
    );
    offensiveMatchupRow.games = games;
    offensiveMatchupRow.isStatRow = true;
    offensiveMatchupRow.stripeGroup = stripeGroup;
    Object.keys(stats).forEach(position => {
      stats[position].forEach(
        stat =>
          (offensiveMatchupRow[`${position}_${stat.stat_abbr}`] =
            stat.stat_value_total),
      );
    });
    // Matchup Row
    const matchupRow = {};
    ({ team_abbr, stats } = matchup.matchup);
    matchupRow.matchups = (
      <TeamMatchupCell team={team_abbr} text={'Ad + / Dis -'} />
    );
    matchupRow.stripeGroup = stripeGroup;
    Object.keys(stats).forEach(position => {
      stats[position].forEach(
        stat =>
          (matchupRow[`${position}_${stat.stat_abbr}`] = stat.stat_value_total),
      );
    });

    Object.keys(matchupRow).forEach(position => {
      matchupRow[position] > 0 ? matchupRow[position] = `+${matchupRow[position]}` : matchupRow[position] = matchupRow[position];
    });

    allStatRows.push(defensiveMatchupRow, offensiveMatchupRow, matchupRow);
    stripeGroup = !stripeGroup;
  });
  return allStatRows;
};

const mapStateToProps = state => {
  const {
    position,
    year,
    formulaId,
    startWeek,
    endWeek,
    matchups,
    hasLoaded,
  } = selectGameStatsMatchupsState(state);
  const {
    toolDescriptions,
    toolsLoaded,
  } = selectToolDescriptionsState(state);
  return {
    hasLoaded,
    // positions: ['QB, RB, WR, TE', 'KICKERS/DEFENSE'],
    selectedPosition: position,
    selectedYear: year,
    selectedStartWeek: startWeek,
    selectedEndWeek: endWeek,
    selectedProfile: formulaId,
    matchups,
    ...(hasLoaded && {
      maxWeek: selectWeeks(state, year, 'projections')[0],
    toolDescriptions,
    toolsLoaded,
    }),
  };
};

const actions = {
  goToView,
  setGameStatsMatchupsPosition,
};

class GameStatsMatchups extends PureComponent {
  static propTypes = {
    hasLoaded: PT.bool.isRequired,
    selectedYear: PT.number,
    selectedProfile: PT.number.isRequired,
    selectedPosition: PT.string.isRequired,
    selectedStartWeek: PT.number,
    selectedEndWeek: PT.number,
    maxWeek: PT.number,
    goToView: PT.func.isRequired,
    setGameStatsMatchupsPosition: PT.func.isRequired,
    matchups: PT.arrayOf(PT.object).isRequired,
    toolsLoaded: PT.bool.isRequired,
    gciPageView: PT.func.isRequired,
  };

  static defaultProps = {
    maxWeek: 17,
    selectedYear: null,
    selectedEndWeek: 17,
    selectedStartWeek: 1,
    colGroups: [],
    tableData: [],
  };

  state = {
    showTeamStats: true,
    selectedPosition: '',
    allStatRows: [],
    colGroupsSetA: [],
    colGroupsSetB: [],
    allStatKeysSetA: [],
    allStatKeysSetB: [],
  };

  componentDidMount = () => {
    const { matchups, selectedPosition } = this.props;
    const { colGroups: colGroupsSetA, allStatKeys: allStatKeysSetA } = processHeaders(matchups, ['QB', 'RB', 'WR', 'TE']);
    const { colGroups: colGroupsSetB, allStatKeys: allStatKeysSetB } = processHeaders(matchups, ['PK', 'DF']);
    document.title = pageTitle + ' - The Huddle';
    gciPageView(pageTitle);
    this.setState({
      allStatRows: processRows(matchups),
      colGroupsSetA,
      colGroupsSetB,
      allStatKeysSetA,
      allStatKeysSetB,
      selectedPosition,
    });
  };

  componentWillReceiveProps = nextProps => {
    if (!this.props.matchups.length && nextProps.matchups.length) {
      const { matchups: nextMatchups } = nextProps;
      const { colGroups: colGroupsSetA, allStatKeys: allStatKeysSetA } = processHeaders(nextMatchups, ['QB', 'RB', 'WR', 'TE']);
      const { colGroups: colGroupsSetB, allStatKeys: allStatKeysSetB } = processHeaders(nextMatchups, ['PK', 'DF']);
      this.setState({
        colGroupsSetA,
        colGroupsSetB,
        allStatKeysSetA,
        allStatKeysSetB,
      });
    }
    if (nextProps.matchups && this.props.matchups) {
      const { matchups: nextMatchups } = nextProps;
      const { matchups: prevMatchups } = this.props;
      if (!isEqual(nextMatchups, prevMatchups)) {
        this.setState({
          allStatRows: processRows(nextMatchups),
        });
      }
    }
    if (nextProps.selectedPosition && this.props.selectedPosition) {
      if (nextProps.selectedPosition !== this.props.selectedPosition) {
        this.setState({ selectedPosition: nextProps.selectedPosition });
      }
    }
  };

  handleChangeYear = (event, { value }) => {
    const { selectedYear, selectedProfile } = this.props;

    if (selectedYear !== value) {
      document.location = '/stats/game-stat-matchups' + '?year=' + value + '&formulaId=' + selectedProfile;
    //   this.props.goToView(GAME_STATS_MATCHUPS_VIEW, {
    //     query: { year: value, formulaId: selectedProfile },
    //     updateQuery: false,
    //   });
    }
  };

  handleChangeProfile = (event, { value }) => {
    if (this.props.selectedProfile !== value) {
      this.props.goToView(GAME_STATS_MATCHUPS_VIEW, {
        query: { formulaId: value },
      });
    }
  };

  handleChangePosition = (event, { value }) => {
    if (this.props.selectedPosition !== value) {
      // this.props.goToView(GAME_STATS_MATCHUPS_VIEW, { query: { position: value } });
      this.props.setGameStatsMatchupsPosition({ position: value });
    }
  };

  handleChangeWeek = range => {
    const { min, max } = range;
    const { selectedStartWeek, selectedEndWeek } = this.props;
    if (min !== selectedStartWeek || max !== selectedEndWeek) {
      this.props.goToView(GAME_STATS_MATCHUPS_VIEW, {
        query: { startWeek: min, endWeek: max },
      });
    }
  };

  toggleTeamStats = () =>
    this.setState(prevState => ({ showTeamStats: !prevState.showTeamStats }));

  buttonMenuChoices = [
    { name: 'QB, RB, WR, TE', value: 'colGroupsSetA' },
    { name: 'KICKERS/DEFENSE', value: 'colGroupsSetB' },
  ];

  render() {
    const {
      selectedYear,
      selectedProfile,
      selectedPosition,
      maxWeek,
      hasLoaded,
      selectedStartWeek,
      selectedEndWeek,
      toolDescriptions,
      toolsLoaded,
    } = this.props;
    const {
      showTeamStats,
      allStatRows,
      colGroupsSetA,
      colGroupsSetB,
      allStatKeysSetA,
      allStatKeysSetB,
    } = this.state;
    const colGroups =
      selectedPosition === 'colGroupsSetA' ? colGroupsSetA : colGroupsSetB;
    const tableData = showTeamStats
      ? allStatRows
      : allStatRows.filter(row => !row.isStatRow);
    const allStatsKeys = selectedPosition === 'colGroupsSetA' ? allStatKeysSetA : allStatKeysSetB;
    return (
      <div className={classes.wrapper}>
        <FiltersHeader
          title={pageTitle}
          description={toolsLoaded && toolDescriptions.tool_descriptions.matchup.visible == 1 && (
            <ToolDescription
              descr={toolDescriptions.tool_descriptions.matchup.text}
            />)}
          seasonType="seasons"
          statType="projections"
          selectedYear={selectedYear}
          selectedProfile={selectedProfile}
          onChangeProfile={this.handleChangeProfile}
          onChangeYear={this.handleChangeYear}
        >
          <FilterRangeSlider
            value={{ min: selectedStartWeek, max: selectedEndWeek }}
            minValue={1}
            maxValue={selectedYear >= 2021 ? 18 : 17}
            sliderLabel="Show Range:"
            onChangeComplete={this.handleChangeWeek}
          />
          <ButtonMenu
            choices={this.buttonMenuChoices}
            label="Stat Type:"
            activeItem={selectedPosition}
            onChange={this.handleChangePosition}
          />
          <FilterToggle
            label={'Show Team Stats:'}
            checked={showTeamStats}
            valueOff={'NO'}
            valueOn={'YES'}
            onChange={this.toggleTeamStats}
          />
        </FiltersHeader>
        <TableControls colGroups={allStatsKeys} />
        {hasLoaded && (
          <PlainTable
            className={classes.tableWrapper}
            colGroups={colGroups}
            data={tableData}
            disableSort
            toggleTeamStats={showTeamStats}
            tableTitleFixed={true}
            tableStatsFixed={true}
          />
        )}
      </div>
    );
  }
}
export default connect(mapStateToProps, actions)(GameStatsMatchups);



// WEBPACK FOOTER //
// ./src/app/views/GameStatsMatchups/GameStatsMatchups.js